import { signupInputSchema } from '@beta-assur/shared';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Field } from '../components/page';
import { ActionError } from '../components/states';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox, Input, Select } from '../components/ui/input';
import { useApi } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { fieldErrors } from './portfolio-forms';

// Inscription d'une agence (BA-088) : demande étudiée par Beta Suite, qui ouvre l'espace en
// essai gratuit de 30 jours et envoie l'invitation de l'administrateur.

const EMPTY = {
  agencyName: '',
  agentCode: '',
  taxId: '',
  city: '',
  insurers: '',
  adminName: '',
  email: '',
  phone: '',
  planCode: 'PRO' as 'ESSENTIEL' | 'PRO' | 'RESEAU',
  acceptTerms: false,
  website: '',
};

export function SignupPage() {
  const { t } = useI18n();
  const api = useApi();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<Error | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (key: keyof typeof EMPTY) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const { agentCode, website, ...rest } = form;
    const parsed = signupInputSchema.safeParse({
      ...rest,
      ...(agentCode.trim() ? { agentCode } : {}),
      ...(website ? { website } : {}),
    });
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    setError(null);
    setPending(true);
    try {
      await api.request('POST', '/v1/public/signup', { body: parsed.data });
      setDone(true);
    } catch (err) {
      setError(err as Error);
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardContent className="flex flex-col gap-4 p-6">
          <h1 className="text-xl font-semibold">{t.signup.title}</h1>
          {done ? (
            <>
              <p role="status" className="text-sm">
                {t.signup.received}
              </p>
              <Link to="/connexion" className={buttonVariants({ variant: 'outline' })}>
                {t.auth.goToLogin}
              </Link>
            </>
          ) : (
            <form
              onSubmit={(e) => void submit(e)}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              noValidate
            >
              <p className="text-sm text-muted-foreground sm:col-span-2">{t.signup.intro}</p>
              <Field label={t.signup.agencyName} error={errors.agencyName}>
                <Input
                  value={form.agencyName}
                  onChange={set('agencyName')}
                  autoComplete="organization"
                />
              </Field>
              <Field label={t.signup.agentCode} hint={t.signup.optional}>
                <Input value={form.agentCode} onChange={set('agentCode')} />
              </Field>
              <Field label={t.signup.taxId} hint={t.signup.taxIdHint} error={errors.taxId}>
                <Input value={form.taxId} onChange={set('taxId')} dir="ltr" />
              </Field>
              <Field label={t.signup.city} error={errors.city}>
                <Input value={form.city} onChange={set('city')} />
              </Field>
              <Field label={t.signup.insurers} hint={t.signup.insurersHint} error={errors.insurers}>
                <Input value={form.insurers} onChange={set('insurers')} />
              </Field>
              <Field label={t.signup.plan}>
                <Select value={form.planCode} onChange={set('planCode')}>
                  {(['ESSENTIEL', 'PRO', 'RESEAU'] as const).map((code) => (
                    <option key={code} value={code}>
                      {t.signup.plans[code]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t.signup.adminName} error={errors.adminName}>
                <Input value={form.adminName} onChange={set('adminName')} autoComplete="name" />
              </Field>
              <Field label={t.auth.email} error={errors.email}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  autoComplete="email"
                  dir="ltr"
                />
              </Field>
              <Field label={t.signup.phone} error={errors.phone}>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  autoComplete="tel"
                  dir="ltr"
                />
              </Field>
              {/* Champ piège, invisible pour une personne. */}
              <div className="hidden" aria-hidden>
                <label>
                  Site web
                  <input
                    tabIndex={-1}
                    value={form.website}
                    onChange={set('website')}
                    autoComplete="off"
                  />
                </label>
              </div>
              <label className="flex items-start gap-2 text-sm sm:col-span-2">
                <Checkbox
                  checked={form.acceptTerms}
                  onChange={(e) => setForm((f) => ({ ...f, acceptTerms: e.target.checked }))}
                />
                {t.signup.terms}
              </label>
              {errors.acceptTerms ? (
                <p className="text-xs text-destructive sm:col-span-2">{errors.acceptTerms}</p>
              ) : null}
              <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
                <Button type="submit" disabled={pending}>
                  {t.signup.submit}
                </Button>
                <Link to="/connexion" className="text-sm text-primary hover:underline">
                  {t.signup.haveAccount}
                </Link>
              </div>
              <div className="sm:col-span-2">
                <ActionError error={error} />
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
